/**
 * WebSocket chat-frame shapes broadcast by the backend (``apps.orders.broadcast``).
 *
 * The per-order chat socket (``/ws/orders/<id>/chat/``) emits ``chat_message`` frames
 * carrying a discriminating ``type``. These are realtime *notifications* — durable truth
 * is the REST/service layer, so chat history is also fetched over REST (a reconnecting
 * socket may miss frames).
 */
import type { OrderChatMessage } from "@entities/order";

/** A chat message broadcast to an order's group (``order_<id>``). */
export interface ChatMessageFrame {
  readonly type: "chat_message";
  readonly id: number;
  readonly order_id: number;
  readonly sender_id: number | null;
  readonly sender_role: string;
  readonly text: string;
  readonly attachment_url: string;
  readonly source: string;
  readonly created_at: string;
}

/** Outgoing frame the client sends over the chat socket. */
export interface OutgoingChatFrame {
  readonly text: string;
  readonly attachment_url?: string;
}

export function isChatMessageFrame(frame: {
  readonly type?: unknown;
}): frame is ChatMessageFrame {
  return frame.type === "chat_message";
}

/** Project a broadcast frame onto the persisted message shape used by the list. */
export function frameToMessage(frame: ChatMessageFrame): OrderChatMessage {
  return {
    id: frame.id,
    order: frame.order_id,
    sender: frame.sender_id,
    sender_role: frame.sender_role,
    text: frame.text,
    attachment_url: frame.attachment_url,
    source: frame.source,
    is_read: false,
    created_at: frame.created_at,
  };
}
